import api from './api';
import axios from 'axios';

export interface Package {
  id: string;
  name: string;
  description: string;
  github_url: string;
  root_dir: string;  // Root directory path within repo
  latest_version: string;
  downloads: number;
  author: string;
  is_published: boolean;
}

export interface PackageWithDetails extends Package {
  license: string;
  created_at: string;
  updated_at: string;
  versions: Array<{
    version: string;
    published_at: string;
  }>;
  readme: string;
}

export interface PackageSearchResults {
  results: Package[];
  total: number;
}

export interface SearchParams {
  query?: string;
  skip?: number;
  limit?: number;
  sort?: 'downloads' | 'name' | 'latest_version' | 'created_at';
  order?: 'asc' | 'desc';
}

// Create a public API instance that doesn't require auth for public endpoints
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*'
  },
  // Use withCredentials with specific origins CORS
  withCredentials: true,
});

// Add response interceptor to handle CORS
publicApi.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 0) {
      console.error('CORS error detected:', error);
    }
    return Promise.reject(error);
  }
);

/**
 * Fetch all packages - public endpoint that doesn't require authentication
 */
export const getPackages = async (skip = 0, limit = 20): Promise<PackageSearchResults> => {
  try {
    const response = await publicApi.get<Package[]>('/packages/list', {
      params: { skip, limit }
    });
    // Wrap the response in a PackageSearchResults format to make it compatible with searchPackages
    return {
      results: response.data,
      total: response.data.length // In a real implementation, this would come from the API
    };
  } catch (error) {
    console.error('Error fetching packages:', error);
    throw error;
  }
};

/**
 * Search packages - public endpoint that doesn't require authentication
 */
export const searchPackages = async ({ query = '', skip = 0, limit = 10, sort = 'downloads', order = 'desc' }: SearchParams) => {
  try {
    const response = await publicApi.get<PackageSearchResults>('/packages/search', {
      params: { query, skip, limit, sort, order }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching packages:', error);
    throw error;
  }
};

/**
 * Get package details - public endpoint that doesn't require authentication
 */
export const getPackageDetails = async (packageId: string) => {
  try {
    const response = await publicApi.get<PackageWithDetails>(`/packages/${packageId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching package details for ${packageId}:`, error);
    throw error;
  }
};

/**
 * Create a new package
 */
export const createPackage = async (packageData: { name: string; description: string; github_url: string; version: string }) => {
  try {
    const response = await api.post<Package>('/packages/create', packageData);
    return response.data;
  } catch (error) {
    console.error('Error creating package:', error);
    throw error;
  }
};

/**
 * Update a package
 */
export const updatePackage = async (
  packageId: string,
  packageData: Partial<{ name: string; description: string; github_url: string }>
) => {
  try {
    const response = await api.put<Package>(`/packages/${packageId}`, packageData);
    return response.data;
  } catch (error) {
    console.error(`Error updating package ${packageId}:`, error);
    throw error;
  }
};

/**
 * Delete a package
 */
export const deletePackage = async (packageId: string) => {
  try {
    const response = await api.delete(`/packages/${packageId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting package ${packageId}:`, error);
    throw error;
  }
};

/**
 * Publish a package (make it publicly available)
 */
export const publishPackage = async (packageId: string) => {
  try {
    const response = await api.post<Package>(`/packages/${packageId}/publish`, {});
    return response.data;
  } catch (error) {
    console.error(`Error publishing package ${packageId}:`, error);
    throw error;
  }
};

/**
 * Unpublish a package (make it private)
 */
export const unpublishPackage = async (packageId: string) => {
  try {
    const response = await api.post<Package>(`/packages/${packageId}/unpublish`, {});
    return response.data;
  } catch (error) {
    console.error(`Error unpublishing package ${packageId}:`, error);
    throw error;
  }
};

/**
 * Release a new version of a package
 */
export const releaseVersion = async (
  packageId: string, 
  versionData: { version: string; changes: string }
) => {
  try {
    const response = await api.post<Package>(`/packages/${packageId}/versions`, versionData);
    return response.data;
  } catch (error) {
    console.error(`Error releasing new version for package ${packageId}:`, error);
    throw error;
  }
};
